import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Check, X } from 'lucide-react';

import CountUp from '../../../components/animations/CountUp';

// Pricing-comparison section (2026-05-22) — replaces CostComparisonSection
// in the homepage slot. Anchored to a real Dayton, OH project (April 2, 2026
// PRNewswire press release), so the numbers and source line are concrete,
// not illustrative.
//
// Pass 1 = visual look-and-feel (gradients, glows, depth — committed).
// Pass 2 (this file's current state) = animations:
//   - Single trigger point: section fires "visible" variant when 10% in view,
//     all children with named variants reveal in a choreographed sequence.
//   - Header eyebrow → headline → subhead (quick stagger).
//   - Markup callout fades+slides up; the "82-122%" number scales 0.8 → 1.05
//     → 1.0 with a red glow that ignites mid-keyframe; body text fades after.
//   - Traditional card slides in from the left; OpenbooQ card slides from the
//     right ~200ms later and its full cyan-glow shadow ignites as it lands.
//   - Prices count up (fast, ~0.7s) as their cards finish settling.
//   - List items stagger in at reading pace (100ms apart). OpenbooQ checkmark
//     discs scale-in with spring physics; Traditional X items just fade.
//   - Source attribution fades in calmly.
//   - The green "You keep" payoff bar slides up LAST, with the savings figure
//     counting up — emotional peak of the section.
//   - Dark CTA bar slides up alongside the payoff.
//
// Skipped from the brief: continuous "breathing" glow on the OpenbooQ card +
// green bar (iteration budget call — variant-driven keyframe arrays + hover
// overrides produced unhover-snapback jank in testing). The static
// elevation + hover-intensify already lifts the OpenbooQ card visually;
// can be added back via a separate motion layer in a follow-up if missed.

const PRICE_TRADITIONAL = 17579;
const PRICE_OPENBOOQ = 9499;
const SAVINGS = PRICE_TRADITIONAL - PRICE_OPENBOOQ; // $8,079
const SAVINGS_PCT = Math.round((SAVINGS / PRICE_TRADITIONAL) * 100); // 46%

// Currency format: $17,579 / $9,499 / $8,079. Math.round handles the
// fractional values that CountUp passes during the in-flight animation.
const formatCurrency = (n) => `$${Math.round(n).toLocaleString('en-US')}`;

// Same ease-out cubic the FadeIn primitive uses elsewhere on the site.
const EASE_OUT = [0.16, 1, 0.3, 1];

// Lower-than-default viewport threshold for the CountUps so their internal
// useInView fires at the same time as the section's parent trigger — the
// element's bounding rect overlaps the viewport at section-entry even when
// its card hasn't visually slid in yet.
const COUNTUP_AMOUNT = 0.1;

// ─── Variants ────────────────────────────────────────────────────────────
// All variants use the same parent-trigger pattern: the <motion.section>
// at the bottom holds initial="hidden" whileInView="visible", and every
// motion descendant with `variants` inherits the state change. Each variant
// carries its own delay/duration so the choreography plays from one trigger
// regardless of scroll speed.

const headerEyebrowV = {
  hidden: { opacity: 0, y: 8 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4, delay: 0, ease: EASE_OUT } },
};

const headerHeadlineV = {
  hidden: { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, delay: 0.08, ease: EASE_OUT } },
};

const headerSubheadV = {
  hidden: { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4, delay: 0.18, ease: EASE_OUT } },
};

const markupCalloutV = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.55, delay: 0.30, ease: EASE_OUT } },
};

// 82-122% — the "shock" beat. Scale overshoot + filter glow ignite.
const markupNumberV = {
  hidden: { opacity: 0, scale: 0.8, filter: 'drop-shadow(0 0 0 rgba(220, 38, 38, 0))' },
  visible: {
    opacity: 1,
    scale: [0.8, 1.05, 1],
    filter: [
      'drop-shadow(0 0 0 rgba(220, 38, 38, 0))',
      'drop-shadow(0 8px 36px rgba(220, 38, 38, 0.55))',
      'drop-shadow(0 8px 28px rgba(220, 38, 38, 0.32))',
    ],
    transition: {
      duration: 0.65,
      delay: 0.45,
      ease: EASE_OUT,
      times: [0, 0.6, 1],
    },
  },
};

const markupBodyV = {
  hidden: { opacity: 0, x: 12 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.45, delay: 0.55, ease: EASE_OUT } },
};

// Traditional card slides in from the left — modest x offset so the slide
// reads cleanly on mobile too (where the card is full-width-stacked).
const traditionalCardV = {
  hidden: { opacity: 0, x: -24 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.5, delay: 0.85, ease: EASE_OUT } },
};

// OpenbooQ card — slides in from the right, ~200ms after Traditional. The
// box-shadow animates from "no shadow" to the full Pass 1 multi-layer glow,
// so the cyan halo + green undershadow visually ignite as the card lands.
const OPENBOOQ_REST_SHADOW =
  '0 0 0 4px rgba(7, 176, 233, 0.10),' +
  ' 0 16px 48px rgba(7, 176, 233, 0.28),' +
  ' 0 28px 64px rgba(16, 185, 129, 0.16),' +
  ' 0 6px 16px rgba(0, 0, 0, 0.06)';

const OPENBOOQ_HOVER_SHADOW =
  '0 0 0 4px rgba(7, 176, 233, 0.25),' +
  ' 0 24px 72px rgba(7, 176, 233, 0.50),' +
  ' 0 32px 80px rgba(16, 185, 129, 0.22),' +
  ' 0 8px 20px rgba(0, 0, 0, 0.08)';

const openbooqCardV = {
  hidden: { opacity: 0, x: 24, boxShadow: '0 0 0 0 rgba(7, 176, 233, 0)' },
  visible: {
    opacity: 1,
    x: 0,
    boxShadow: OPENBOOQ_REST_SHADOW,
    transition: { duration: 0.55, delay: 1.05, ease: EASE_OUT },
  },
};

// List-item variants — index-driven via the `custom` prop so each row
// staggers off the same parent trigger. ~100ms apart reads as "reading
// pace" without dragging.
const traditionalItemV = {
  hidden: { opacity: 0, x: -8 },
  visible: (i) => ({
    opacity: 1,
    x: 0,
    transition: { duration: 0.35, delay: 1.30 + i * 0.10, ease: EASE_OUT },
  }),
};

// OpenbooQ row text — same fade as Traditional. The check disc scale is
// a separate variant (below) so the icon pop happens slightly after the
// row appears, adding the "drawing in" feel.
const openbooqItemV = {
  hidden: { opacity: 0, x: 8 },
  visible: (i) => ({
    opacity: 1,
    x: 0,
    transition: { duration: 0.35, delay: 1.45 + i * 0.10, ease: EASE_OUT },
  }),
};

const openbooqCheckV = {
  hidden: { scale: 0 },
  visible: (i) => ({
    scale: 1,
    transition: {
      delay: 1.50 + i * 0.10,
      type: 'spring',
      stiffness: 360,
      damping: 18,
    },
  }),
};

const sourceV = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.4, delay: 1.75, ease: EASE_OUT } },
};

// Green "You keep" bar — the payoff peak. Slides up after everything else
// settles. Sits inside the OpenbooQ card so the variant fires when its
// nearest motion ancestor's "visible" state activates (i.e., the section's),
// not when the card's own opacity transition ends.
const greenBarV = {
  hidden: { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.55, delay: 2.00, ease: EASE_OUT } },
};

const ctaBarV = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.55, delay: 2.20, ease: EASE_OUT } },
};

// ─── Click handlers ─────────────────────────────────────────────────────

// Clicking the CTA scrolls to the hero address bar (id="hero-form"). Same
// pattern the Zero Section's "See your $0-down price" button uses.
const handleCtaClick = (e) => {
  e.preventDefault();
  const target = document.getElementById('hero-form');
  if (target) {
    target.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }
};

// ─── Component ──────────────────────────────────────────────────────────

const PricingComparisonSection = () => (
  <motion.section
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true, amount: 0.10 }}
    className="py-16 lg:py-24 relative overflow-hidden"
    style={{
      // Subtle cyan radial centered on the price-cards row lifts the cards
      // off the page without muddying the white background.
      background: 'radial-gradient(ellipse 65% 45% at 50% 55%, rgba(7, 176, 233, 0.06) 0%, transparent 60%), white',
    }}
  >
    <div className="max-w-[1200px] mx-auto px-5 lg:px-8 relative">

      {/* A. Section header */}
      <div className="text-center mb-12 lg:mb-16">
        <motion.p
          variants={headerEyebrowV}
          className="text-xs font-bold uppercase tracking-[0.2em] mb-4"
          style={{ color: '#07B0E9' }}
        >
          Real numbers, real homes
        </motion.p>
        <motion.h2
          variants={headerHeadlineV}
          className="text-3xl sm:text-4xl lg:text-5xl font-black mb-4 leading-[1.1] tracking-tight"
          style={{ color: '#000150', fontFamily: "'Poppins', 'Montserrat', sans-serif", letterSpacing: '-0.02em' }}
        >
          Why does roofing cost so much?
        </motion.h2>
        <motion.p
          variants={headerSubheadV}
          className="text-gray-500 text-base lg:text-lg max-w-2xl mx-auto leading-relaxed"
        >
          It doesn&apos;t have to. Here&apos;s exactly what you&apos;re paying for at a traditional roofer — and what we charge for the same roof.
        </motion.p>
      </div>

      {/* B. The 82-122% markup callout — number left, copy right on desktop;
            stacked vertically on mobile. */}
      <motion.div
        variants={markupCalloutV}
        className="rounded-2xl p-6 sm:p-8 lg:p-10 mb-10 lg:mb-12 flex flex-col md:flex-row md:items-center gap-6 md:gap-10 relative"
        style={{
          background: 'rgba(254, 226, 226, 0.55)',
          border: '1px solid rgba(220, 38, 38, 0.20)',
          // Inset soft red wash gives the card vignette depth instead of
          // reading as a flat tinted rectangle; outer red-tinted shadow
          // grounds it.
          boxShadow: 'inset 0 0 80px rgba(220, 38, 38, 0.06), 0 12px 36px rgba(220, 38, 38, 0.10)',
        }}
      >
        <motion.div
          variants={markupNumberV}
          className="font-black leading-none text-center md:text-left flex-shrink-0"
          style={{
            // Red gradient mirrors the cyan number gradient elsewhere on the
            // site (Zero Section $0, Trust Bridge stats) — top-light to
            // bottom-dark over a 3-stop linear. The drop-shadow is driven by
            // the variant's `filter` keyframes, not a static value here, so
            // the red glow ignites mid-entrance.
            background: 'linear-gradient(180deg, #F87171 0%, #DC2626 50%, #7F1D1D 100%)',
            WebkitBackgroundClip: 'text',
            backgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            color: 'transparent',
            fontSize: 'clamp(64px, 11vw, 128px)',
            letterSpacing: '-0.04em',
          }}
        >
          82-122%
        </motion.div>
        <motion.div variants={markupBodyV} className="flex-1">
          <h3
            className="text-xl lg:text-2xl font-black mb-3 leading-tight"
            style={{ color: '#000150' }}
          >
            That&apos;s the markup hiding in traditional roofing quotes.
          </h3>
          <p className="text-sm lg:text-base text-gray-600 leading-relaxed">
            If your materials and labor genuinely cost <strong className="font-bold text-gray-900">$10,000</strong>, a traditional roofer charges you <strong className="font-bold text-gray-900">$18,200-$23,300</strong>. The extra $8,200-$13,300 is sales commission, marketing overhead, and profit margin — buried, not itemized.
          </p>
        </motion.div>
      </motion.div>

      {/* C. Two price comparison cards — Traditional left, OpenbooQ right
            (winner). Stack vertically on mobile in the same order, so the
            reader sees the setup before the payoff. */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 lg:gap-6 mb-4">

        {/* Traditional Roofer card — intentionally receded vs the OpenbooQ
            card below. Lower-alpha pink wash + softer, flatter shadow so it
            reads as "the alternative" rather than competing for attention. */}
        <motion.article
          variants={traditionalCardV}
          whileHover={{ y: -4 }}
          transition={{ y: { duration: 0.25, ease: 'easeOut' } }}
          className="rounded-2xl p-6 lg:p-8 flex flex-col"
          style={{
            background: 'rgba(254, 226, 226, 0.32)',
            border: '1px solid rgba(220, 38, 38, 0.14)',
            boxShadow: '0 4px 14px rgba(0, 0, 0, 0.04)',
          }}
        >
          <span
            className="self-start inline-flex items-center px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-widest mb-4"
            style={{
              background: 'rgba(220, 38, 38, 0.12)',
              color: '#DC2626',
              border: '1px solid rgba(220, 38, 38, 0.25)',
            }}
          >
            Traditional Roofer
          </span>
          <h3 className="text-lg lg:text-xl font-bold text-gray-900 leading-tight">
            Same roof. Same shingles. Same square footage.
          </h3>
          <p className="text-sm text-gray-500 mt-1 mb-5">Competitor quote — Dayton, OH</p>
          <CountUp
            to={PRICE_TRADITIONAL}
            duration={0.7}
            delay={1.10}
            amount={COUNTUP_AMOUNT}
            format={formatCurrency}
            className="font-black leading-none mb-6 block"
            style={{
              // Red gradient matches the 82-122% callout vocabulary.
              background: 'linear-gradient(180deg, #F87171 0%, #DC2626 55%, #991B1B 100%)',
              WebkitBackgroundClip: 'text',
              backgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              color: 'transparent',
              fontSize: 'clamp(48px, 8vw, 72px)',
              letterSpacing: '-0.03em',
              filter: 'drop-shadow(0 4px 14px rgba(220, 38, 38, 0.22))',
              display: 'block',
            }}
          />
          <ul className="space-y-3 mt-auto">
            {[
              'Sales commissions baked in',
              'Material markup of 50-100%',
              'Hidden labor margins',
              '30-50% deposit at signing',
            ].map((item, i) => (
              <motion.li
                key={item}
                variants={traditionalItemV}
                custom={i}
                className="flex items-start gap-3"
              >
                <span
                  className="flex items-center justify-center w-5 h-5 rounded-full flex-shrink-0 mt-0.5"
                  style={{ background: 'rgba(220, 38, 38, 0.18)' }}
                  aria-hidden="true"
                >
                  <X size={12} strokeWidth={3} style={{ color: '#DC2626' }} />
                </span>
                <span className="text-sm text-gray-700 leading-relaxed">{item}</span>
              </motion.li>
            ))}
          </ul>
        </motion.article>

        {/* OpenbooQ card — the answer / winner. Deeper cyan bg gradient,
            layered halo (4px soft ring just outside the border) + elevation
            shadow + green undershadow that suggests the "You keep" bar
            below is the source. The box-shadow ignites from "no shadow" to
            the full multi-layer glow during entrance (variant-driven).
            whileHover overrides with an even stronger glow + extra lift. */}
        <motion.article
          variants={openbooqCardV}
          whileHover={{ y: -6, boxShadow: OPENBOOQ_HOVER_SHADOW }}
          transition={{
            y: { duration: 0.25, ease: 'easeOut' },
            boxShadow: { duration: 0.25, ease: 'easeOut' },
          }}
          className="rounded-2xl p-6 lg:p-8 flex flex-col overflow-hidden relative"
          style={{
            background: 'linear-gradient(180deg, #F0F9FF 0%, #DBEAFE 100%)',
            border: '2px solid #07B0E9',
          }}
        >
          <span
            className="self-start inline-flex items-center px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-widest mb-4"
            style={{
              background: 'rgba(7, 176, 233, 0.15)',
              color: '#07B0E9',
              border: '1px solid rgba(7, 176, 233, 0.40)',
            }}
          >
            OpenbooQ
          </span>
          <h3 className="text-lg lg:text-xl font-bold text-gray-900 leading-tight">
            Same roof. Same shingles. Same warranty.
          </h3>
          <p className="text-sm text-gray-500 mt-1 mb-5">OpenbooQ quote — Dayton, OH</p>
          <CountUp
            to={PRICE_OPENBOOQ}
            duration={0.7}
            delay={1.30}
            amount={COUNTUP_AMOUNT}
            format={formatCurrency}
            className="font-black leading-none mb-6"
            style={{
              // Cyan number gradient — same 3-stop linear used by .bridge-stat-num
              // (Trust Bridge) and the Zero Section's giant $0 graphic, so this
              // price reads as part of the brand vocabulary, not a one-off.
              background: 'linear-gradient(180deg, var(--blue-glow) 0%, #07B0E9 50%, var(--blue-deep) 100%)',
              WebkitBackgroundClip: 'text',
              backgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              color: 'transparent',
              fontSize: 'clamp(48px, 8vw, 72px)',
              letterSpacing: '-0.03em',
              filter: 'drop-shadow(0 4px 16px rgba(7, 176, 233, 0.35))',
              display: 'block',
            }}
          />
          <ul className="space-y-3 mb-6">
            {[
              '$0 commissions — no salespeople',
              '0% material markup — wholesale cost',
              'Real labor cost — line-itemed',
              '$0 down — pay when the roof is on',
            ].map((item, i) => (
              <motion.li
                key={item}
                variants={openbooqItemV}
                custom={i}
                className="flex items-start gap-3"
              >
                <motion.span
                  variants={openbooqCheckV}
                  custom={i}
                  className="flex items-center justify-center w-5 h-5 rounded-full flex-shrink-0 mt-0.5"
                  style={{ background: '#10B981' }}
                  aria-hidden="true"
                >
                  <Check size={12} strokeWidth={3} className="text-white" />
                </motion.span>
                <span className="text-sm text-gray-700 leading-relaxed">{item}</span>
              </motion.li>
            ))}
          </ul>
          {/* "You keep" payoff bar — the emotional peak of the section.
              Brighter-to-deeper green gradient, subtle top-edge highlight
              for dimensionality. The outer green glow lives on the article's
              box-shadow (above) so it can extend beyond the overflow-hidden
              clip and visually suggest light spilling out from this bar.
              Slides up LAST in the choreography (delay 2.00s) so it lands
              after everything else has settled. */}
          <motion.div
            variants={greenBarV}
            className="-mx-6 lg:-mx-8 -mb-6 lg:-mb-8 mt-auto px-6 lg:px-8 py-4 lg:py-5 flex items-center justify-between gap-4 flex-wrap"
            style={{
              background: 'linear-gradient(90deg, #10B981 0%, #047857 100%)',
              color: 'white',
              boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.18), inset 0 -8px 24px rgba(0, 0, 0, 0.10)',
            }}
          >
            <span className="font-bold text-base lg:text-lg">You keep</span>
            <span className="font-black text-xl lg:text-2xl tracking-tight">
              <CountUp
                to={SAVINGS}
                duration={0.65}
                delay={2.10}
                amount={COUNTUP_AMOUNT}
                format={formatCurrency}
              />
              {' '}— {SAVINGS_PCT}% less
            </span>
          </motion.div>
        </motion.article>
      </div>

      {/* D. Source attribution */}
      <motion.p
        variants={sourceV}
        className="text-center text-xs text-gray-400 mb-4 max-w-2xl mx-auto leading-relaxed"
      >
        Actual quotes from a 2026 Dayton, OH project. Source: OpenbooQ press release (PRNewswire, April 2, 2026).
      </motion.p>

      {/* D2. Quote Check crosslink — the reader has just seen the markup
            side by side; if they're holding a competitor quote of their own,
            route them to the free public analyzer at /compare/quote. */}
      <motion.p
        variants={sourceV}
        className="text-center text-sm font-semibold mb-12 lg:mb-16"
      >
        <Link
          to="/compare/quote"
          className="inline-flex items-center gap-1.5 transition-colors hover:underline"
          style={{ color: '#0596C7' }}
        >
          Have a quote in hand? Get it checked free
          <ArrowRight size={15} strokeWidth={2.4} aria-hidden="true" />
        </Link>
      </motion.p>

      {/* E. Dark CTA bar — heading + body + tagline left, button right on
            desktop; stacked + full-width button on mobile. Slides up at
            the tail of the choreography in parallel with the green bar. */}
      <motion.div
        variants={ctaBarV}
        className="rounded-2xl p-6 lg:p-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6 md:gap-10"
        style={{
          background: 'linear-gradient(135deg, #0a1628 0%, #000150 100%)',
          boxShadow: '0 16px 48px rgba(0, 1, 80, 0.25)',
        }}
      >
        <div className="flex-1 text-center md:text-left">
          <h3
            className="text-3xl lg:text-4xl font-black mb-2 leading-tight"
            style={{ color: 'white', letterSpacing: '-0.02em' }}
          >
            See <span style={{ color: '#07B0E9' }}>your</span> number.
          </h3>
          <p
            className="text-sm lg:text-base mb-2 leading-relaxed"
            style={{ color: 'rgba(255, 255, 255, 0.75)' }}
          >
            ~30 minutes from your address to a full, line-itemed quote. $0 down. No high-pressure sales.
          </p>
          <p
            className="italic text-sm font-medium"
            style={{ color: '#07B0E9' }}
          >
            An open book, down to the last dollar.
          </p>
        </div>
        <a
          href="#hero-form"
          onClick={handleCtaClick}
          className="inline-flex items-center justify-center gap-2 px-6 py-4 rounded-2xl font-bold text-base whitespace-nowrap transition-all duration-200 self-stretch md:self-auto"
          style={{
            background: 'linear-gradient(135deg, #07B0E9 0%, #0596C7 100%)',
            color: 'white',
            boxShadow: '0 8px 24px rgba(7, 176, 233, 0.5)',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 12px 32px rgba(7, 176, 233, 0.65)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 8px 24px rgba(7, 176, 233, 0.5)';
          }}
        >
          <span>Get my roof price</span>
          <ArrowRight size={18} strokeWidth={2.4} aria-hidden="true" />
        </a>
      </motion.div>

    </div>
  </motion.section>
);

export default PricingComparisonSection;
